import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface DropdownOption {
  label: string;
  value: string | null;
  isDefault?: boolean;
}

export interface ProviderSelection {
  selectedValues: string[];
  displayText: string;
  isDefault: boolean;
}

@Component({
  selector: 'app-dropdown-checklist',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dropdown-checkbox.component.html',
  styleUrls: ['./dropdown-checkbox.component.scss']
})
export class DropdownChecklistComponent implements OnInit, OnChanges {
  @Input() label = '';
  @Input() placeholder = '== Select an option ==';
  @Input() options: DropdownOption[] = [];
  @Input() showDefaultOption = true;
  @Input() defaultOptionLabel = 'Default - Show All';
  @Input() selectedValues: string[] = [];

  @Output() selectionChange = new EventEmitter<ProviderSelection>();

  isDropdownOpen = false;
  displayText = '';
  defaultChecked = false;
  
  selectedOptions: Map<string, boolean> = new Map();

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.initializeSelections();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedValues'] || changes['options']) {
      this.initializeSelections();
    }
  }

  private initializeSelections(): void {
    const selectedValuesArray = Array.isArray(this.selectedValues)
      ? this.selectedValues.map(val => String(val))
      : (this.selectedValues ? [String(this.selectedValues)] : []);


    // Check if all options are selected or if selectedValues is empty/null
    const allOptionValues = this.getValidOptions().map(opt => opt.value!);
    const allSelected = allOptionValues.length > 0 && 
                       allOptionValues.every(val => selectedValuesArray.includes(String(val)));

    // If selectedValues is empty or null, treat as "All" selected
    if (selectedValuesArray.length === 0) {
      this.defaultChecked = true;
      this.options.forEach(option => {
        if (option.value !== null) {
          this.selectedOptions.set(option.value, true);
        }
      });
      this.displayText = this.defaultOptionLabel;
    } else if (allSelected) {
      // All individual options are selected
      this.defaultChecked = true;
      this.options.forEach(option => {
        if (option.value !== null) {
          this.selectedOptions.set(option.value, true);
        }
      });
      this.displayText = this.defaultOptionLabel;
    } else {
      // Some options are selected
      this.defaultChecked = false;
      this.options.forEach(option => {
        if (option.value !== null) {
          const isSelected = selectedValuesArray.some(selectedVal => 
            String(selectedVal) === String(option.value)
          );
          this.selectedOptions.set(option.value, isSelected);
          // console.log(`Option ${option.label} (value: "${option.value}"): ${isSelected ? 'CHECKED' : 'unchecked'}`);
        }
      });
      this.updateDisplayText();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  isOptionChecked(value: string): boolean {
    return this.selectedOptions.get(value) || false;
  }

  isOptionActive(value: string): boolean {
    return this.isOptionChecked(value);
  }

  onDefaultChange(): void {
    if (this.defaultChecked) {
      // "All" was checked - check all individual options
      this.selectedOptions.forEach((_, key) => {
        this.selectedOptions.set(key, true);
      });
      this.displayText = this.defaultOptionLabel;
    } else {
      // "All" was unchecked - uncheck all individual options
      this.selectedOptions.forEach((_, key) => {
        this.selectedOptions.set(key, false);
      });
      this.displayText = '';
    }
    this.emitSelection();
  }

  onOptionChange(value: string): void {
    const currentState = this.selectedOptions.get(value) || false;
    const newState = !currentState;
    
    // If "All" is currently checked and user is unchecking an option
    if (this.defaultChecked && !newState) {
      // Uncheck "All" first
      this.defaultChecked = false;
      
      // Keep all other options checked except the one being unchecked
      this.selectedOptions.forEach((_, key) => {
        if (key === value) {
          this.selectedOptions.set(key, false);
        } else {
          this.selectedOptions.set(key, true);
        }
      });
      
      this.updateDisplayText();
      this.emitSelection();
      return;
    }
    
    // Normal toggle behavior
    this.selectedOptions.set(value, newState);
    
    // Check if all options are now selected
    const allSelected = this.areAllOptionsSelected();
    
    if (allSelected) {
      // All options are checked, so check "All"
      this.defaultChecked = true;
    } else {
      // Not all options are checked, so ensure "All" is unchecked
      this.defaultChecked = false;
    }
    
    this.updateDisplayText();
    this.emitSelection();
  }

  private areAllOptionsSelected(): boolean {
    const validOptions = this.getValidOptions();
    return validOptions.every(opt => this.selectedOptions.get(opt.value!) === true);
  }

  private updateDisplayText(): void {
    // If "All" is checked, show the default label
    if (this.defaultChecked) {
      this.displayText = this.defaultOptionLabel;
      return;
    }
    
    const selected = this.getSelectedOptions();
    
    if (selected.length === 0) {
      this.displayText = '';
    } else if (selected.length === 1) {
      const option = this.options.find(opt => opt.value === selected[0]);
      this.displayText = option ? option.label : '';
    } else {
      const labels = selected
        .map(val => this.options.find(opt => opt.value === val)?.label)
        .filter(Boolean);
      this.displayText = labels.join(' & ');
    }
  }

  private getSelectedOptions(): string[] {
    const selected: string[] = [];
    this.selectedOptions.forEach((isChecked, value) => {
      if (isChecked) {
        selected.push(value);
      }
    });
    return selected;
  }

  private emitSelection(): void {
    // If "All" is checked (defaultChecked is true), emit empty array to signal null
    // Otherwise emit the selected values
    const selectedValues = this.defaultChecked ? [] : this.getSelectedOptions();
    
    this.selectionChange.emit({
      selectedValues: selectedValues,
      displayText: this.displayText,
      isDefault: this.defaultChecked
    });
    
    console.log('Emitting selection:', {
      selectedValues,
      displayText: this.displayText,
      isDefault: this.defaultChecked
    });
  }

  getValidOptions(): DropdownOption[] {
    return this.options.filter(opt => opt.value !== null);
  }
}